import React, { useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { format, formatDistanceToNow } from '../../utils/datetime';
import {
  Box, Chip, Stack, TextField, Tooltip, Typography, alpha, useTheme,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DnsIcon from '@mui/icons-material/Dns';
import SearchIcon from '@mui/icons-material/Search';
import HourglassBottomIcon from '@mui/icons-material/HourglassBottom';

import { fetchRequest as workspacesFetch } from '../../redux/actions/workspaces/list';

import PageBreadcrumbs from '../common/PageBreadcrumbs';
import DataTable from '../common/DataTable';
import ListingToolbar from '../common/ListingToolbar';
import EntityCell from '../common/EntityCell';
import RowActions from '../common/RowActions';
import { CMButton } from '../../containers/styledComponents';

const safeFormatDate = (d, fmt = 'MMM d, yyyy', fb = '—') => {
  try {
    if (!d) return fb;
    return format(new Date(d), fmt);
  } catch (_e) { return fb; }
};

const safeRelative = (d) => {
  try {
    if (!d) return '';
    return `${formatDistanceToNow(new Date(d))} ago`;
  } catch (_e) { return ''; }
};

// "in 6 days"; "now" once the time has passed.
const safeTimeUntil = (d) => {
  try {
    if (!d) return '';
    const t = new Date(d);
    if (t.getTime() <= Date.now()) return 'now';
    return `in ${formatDistanceToNow(t)}`;
  } catch (_e) { return ''; }
};

// Workspace role -> tone for the EntityCell stripe. Highest-trust role
// (owner) gets the strongest accent.
const ROLE_TONE = {
  owner: 'success',
  admin: 'primary',
  editor: 'info',
  viewer: 'neutral',
  member: 'neutral',
};

const ROLE_FILTER_OPTIONS = [
  { value: 'owner',  label: 'Owner'  },
  { value: 'admin',  label: 'Admin'  },
  { value: 'editor', label: 'Editor' },
  { value: 'viewer', label: 'Viewer' },
];

// Compact "activity row" - one column showing the entity counts as
// inline pills rather than seven separate columns. ML practitioners
// scan a workspace list for "where the work is" - this is enough.
const ActivityCell = ({ workspace }) => {
  const theme = useTheme();
  const stats = workspace.statistics || {};
  const items = [
    { label: 'Pipelines',   value: stats.pipelines_count   || 0 },
    { label: 'Models',      value: stats.models_count || stats.blueprints_count || 0 },
    { label: 'Segments',    value: stats.segments_count    || 0 },
    { label: 'Trainings',   value: stats.trainings_count   || 0 },
    { label: 'Deployments', value: stats.deployments_count || 0 },
    { label: 'Inferences',  value: stats.inferences_count  || 0 },
  ].filter((it) => it.value > 0);

  if (items.length === 0) {
    return <Typography variant="caption" color="text.secondary">No activity yet</Typography>;
  }

  return (
    <Tooltip
      arrow placement="top"
      title={
        <Box sx={{ fontSize: 11 }}>
          {items.map((it) => (
            <Box key={it.label}>{it.value.toLocaleString()} {it.label}</Box>
          ))}
        </Box>
      }
    >
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
        {items.slice(0, 4).map((it) => (
          <Box
            key={it.label}
            component="span"
            sx={{
              display: 'inline-flex',
              alignItems: 'baseline',
              gap: 0.4,
              px: 0.75,
              py: 0.2,
              borderRadius: 0.75,
              bgcolor: alpha(theme.palette.text.primary, theme.palette.mode === 'dark' ? 0.06 : 0.04),
              fontSize: 11,
              lineHeight: 1.4,
            }}
          >
            <Typography component="span" sx={{
              fontWeight: 700,
              fontSize: 11,
              fontVariantNumeric: 'tabular-nums',
              color: theme.palette.text.primary,
            }}>
              {it.value}
            </Typography>
            <Typography component="span" sx={{ fontSize: 10, color: theme.palette.text.secondary }}>
              {it.label.toLowerCase()}
            </Typography>
          </Box>
        ))}
        {items.length > 4 && (
          <Typography component="span" sx={{ fontSize: 11, color: theme.palette.text.secondary, ml: 0.25 }}>
            +{items.length - 4}
          </Typography>
        )}
      </Box>
    </Tooltip>
  );
};

const WorkspacesPage = ({ fetchWorkspaces, payload, isLoading, error }) => {
  const navigate = useNavigate();
  const theme = useTheme();

  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [sortColumn, setSortColumn] = useState('created_at');
  const [sortDirection, setSortDirection] = useState('desc');

  useEffect(() => { fetchWorkspaces(); }, [fetchWorkspaces]);
  useEffect(() => { setPage(0); }, [search, roleFilter]);

  const workspaces = payload?.workspaces || [];

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return workspaces.filter((p) => {
      if (roleFilter.length > 0 && !roleFilter.includes(String(p.user_role || '').toLowerCase())) {
        return false;
      }
      if (!q) return true;
      return (p.name || '').toLowerCase().includes(q)
        || (p.uid || '').toLowerCase().includes(q)
        || (p.description || '').toLowerCase().includes(q);
    });
  }, [workspaces, search, roleFilter]);

  const sorted = useMemo(() => {
    const arr = [...filtered];
    const dir = sortDirection === 'asc' ? 1 : -1;
    arr.sort((a, b) => {
      let av; let bv;
      switch (sortColumn) {
        case 'name': av = (a.name || '').toLowerCase(); bv = (b.name || '').toLowerCase(); break;
        case 'created_at':
        default:
          av = a.created_at ? new Date(a.created_at).getTime() : 0;
          bv = b.created_at ? new Date(b.created_at).getTime() : 0;
      }
      if (av < bv) return -1 * dir;
      if (av > bv) return  1 * dir;
      return 0;
    });
    return arr;
  }, [filtered, sortColumn, sortDirection]);

  const paged = useMemo(
    () => sorted.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [sorted, page, rowsPerPage],
  );

  const handleAddWorkspace = () => navigate('/new-workspace');
  const handleViewWorkspace = (uid) => navigate(`/workspace/${uid}`);

  const toggleRole = (r) =>
    setRoleFilter((prev) => prev.includes(r) ? prev.filter((v) => v !== r) : [...prev, r]);

  const activeFilterCount = (search ? 1 : 0) + (roleFilter.length > 0 ? 1 : 0);
  const clearAll = () => { setSearch(''); setRoleFilter([]); };

  const columns = [
    {
      key: 'identity',
      label: 'ID',
      width: 130,
      render: (workspace) => {
        const role = String(workspace.user_role || '').toLowerCase();
        return (
          <EntityCell
            id={workspace.uid}
            tone={ROLE_TONE[role] || 'neutral'}
            label={role || 'Member'}
          />
        );
      },
    },
    {
      key: 'name',
      label: 'Workspace',
      sortable: true,
      width: '32%',
      render: (workspace) => (
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}>
            <Typography variant="body2" sx={{ fontWeight: 600, fontSize: 13, lineHeight: 1.3 }}>
              {workspace.name || workspace.uid}
            </Typography>
            {workspace.expires_at && (
              <Tooltip
                arrow placement="top"
                title={`Ephemeral - deleted with everything in it on ${safeFormatDate(workspace.expires_at, 'PPpp')}. Open settings to keep it.`}
              >
                <Chip
                  size="small"
                  icon={<HourglassBottomIcon sx={{ fontSize: '12px !important' }} />}
                  label={`expires ${safeTimeUntil(workspace.expires_at)}`}
                  color="warning"
                  variant="outlined"
                  sx={{ height: 18, fontSize: 10.5, '& .MuiChip-label': { px: 0.75 } }}
                />
              </Tooltip>
            )}
          </Box>
          {workspace.description && (
            <Typography variant="caption" color="text.secondary" sx={{
              display: 'block', fontSize: 11, mt: 0.25,
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
              maxWidth: 420,
            }}>
              {workspace.description}
            </Typography>
          )}
        </Box>
      ),
    },
    {
      key: 'activity',
      label: 'Activity',
      width: '32%',
      render: (workspace) => <ActivityCell workspace={workspace} />,
    },
    {
      key: 'created_at',
      label: 'Created',
      sortable: true,
      width: 140,
      render: (workspace) => (
        <Tooltip title={safeFormatDate(workspace.created_at, 'PPpp')} arrow placement="top">
          <Box>
            <Typography variant="body2" sx={{ fontSize: 13 }}>
              {safeFormatDate(workspace.created_at)}
            </Typography>
            {workspace.created_at && (
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: 11 }}>
                {safeRelative(workspace.created_at)}
              </Typography>
            )}
          </Box>
        </Tooltip>
      ),
    },
    {
      key: 'actions',
      label: '',
      width: 60,
      align: 'right',
      stopRowClick: true,
      render: (workspace) => (
        <RowActions onOpen={() => handleViewWorkspace(workspace.uid)} openLabel="Open workspace" />
      ),
    },
  ];

  if (error) {
    return (
      <Box sx={{ width: '100%' }}>
        <PageBreadcrumbs items={[{ text: 'Workspaces', icon: <DnsIcon /> }]} sx={{ mb: 2 }} />
        <Box sx={{ p: 3, bgcolor: alpha(theme.palette.error.main, 0.08), color: theme.palette.error.main, borderRadius: 1 }}>
          <Typography variant="h6">Error loading workspaces</Typography>
          <Typography variant="body2">{String(error)}</Typography>
        </Box>
      </Box>
    );
  }

  const filtersNode = (
    <>
      <TextField
        size="small"
        placeholder="Search workspaces by name…"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        InputProps={{
          startAdornment: <SearchIcon sx={{ fontSize: 18, mr: 1, color: 'text.secondary' }} />,
        }}
        sx={{ minWidth: 240, flex: 1 }}
      />
      <Stack direction="row" spacing={0.5} alignItems="center" flexWrap="wrap" useFlexGap>
        <Typography variant="caption" color="text.secondary" sx={{
          fontWeight: 600, letterSpacing: 0.4, textTransform: 'uppercase', mr: 0.5,
        }}>
          Role
        </Typography>
        {ROLE_FILTER_OPTIONS.map((opt) => {
          const selected = roleFilter.includes(opt.value);
          return (
            <Chip
              key={opt.value}
              label={opt.label}
              size="small"
              onClick={() => toggleRole(opt.value)}
              color={selected ? 'primary' : 'default'}
              variant={selected ? 'filled' : 'outlined'}
              sx={{ cursor: 'pointer' }}
            />
          );
        })}
      </Stack>
    </>
  );

  const actionsNode = (
    <CMButton startIcon={<AddIcon />} onClick={handleAddWorkspace} size="small">
      New workspace
    </CMButton>
  );

  return (
    <Box sx={{ width: '100%' }}>
      <PageBreadcrumbs items={[{ text: 'Workspaces', icon: <DnsIcon /> }]} mb={0.5} />

      <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5, ml: 1 }}>
        Each workspace is an isolated environment with its own segments, models, pipelines, and deployments.
      </Typography>

      <ListingToolbar
        filters={filtersNode}
        actions={actionsNode}
        activeCount={activeFilterCount}
        onClearFilters={clearAll}
        trailing={activeFilterCount > 0 ? `${sorted.length} of ${workspaces.length}` : null}
      />

      <DataTable
        columns={columns}
        rows={paged}
        getRowKey={(row) => row.uid}
        onRowClick={(row) => handleViewWorkspace(row.uid)}
        loading={isLoading || !payload}
        sortColumn={sortColumn}
        sortDirection={sortDirection}
        onSortChange={(key, dir) => { setSortColumn(key); setSortDirection(dir); }}
        page={page}
        rowsPerPage={rowsPerPage}
        totalItems={sorted.length}
        onPageChange={setPage}
        onRowsPerPageChange={(n) => { setRowsPerPage(n); setPage(0); }}
        emptyState={{
          icon: <DnsIcon sx={{ fontSize: 32 }} />,
          title: activeFilterCount > 0 ? 'No workspaces match your filters' : 'No workspaces yet',
          message: activeFilterCount > 0
            ? 'Try a different search term or clear the role filter.'
            : 'Create your first workspace to start building pipelines and deploying models.',
          action: activeFilterCount > 0 ? null : (
            <CMButton startIcon={<AddIcon />} onClick={handleAddWorkspace}>
              Create your first workspace
            </CMButton>
          ),
        }}
      />
    </Box>
  );
};

const mapStateToProps = (state) => ({
  payload: state.workspace_list.response,
  isLoading: state.workspace_list.isLoading,
  error: state.workspace_list.error,
});

const mapDispatchToProps = (dispatch) => ({
  fetchWorkspaces: () => dispatch(workspacesFetch({})),
});

export default connect(mapStateToProps, mapDispatchToProps)(WorkspacesPage);
